// src/components/ImageGenerationScreen.tsx
import React, { useState, useEffect, ChangeEvent } from "react";
import {
  ChevronLeft,
  Image,
  Download,
  Copy,
  RefreshCw,
  Sliders,
  CreditCard,
  Loader,
} from "lucide-react";
import { ApiClient } from "../track/apiClient";

// 環境変数用の型定義を追加
declare global {
  namespace NodeJS {
    interface ProcessEnv {
      REACT_APP_API_BASE_URL?: string;
      REACT_APP_API_TOKEN?: string;
    }
  }
}

interface ImageGenerationScreenProps {
  onBack: () => void;
  userId: string;
}

interface StyleOption {
  id: string;
  name: string;
}

interface SizeOption {
  id: string;
  name: string;
  aspect: string;
  width: number;
  height: number;
}

interface QualityOption {
  id: string;
  name: string;
  steps: number;
}

interface GenerationHistoryItem {
  url: string;
  prompt: string;
}

// Flux APIリクエスト用の型定義
interface FluxRequestData {
  prompt: string;
  negative_prompt?: string;
  width?: number;
  height?: number;
  num_outputs?: number;
  scheduler?: string;
  num_inference_steps?: number;
  guidance_scale?: number;
  prompt_upsampling?: boolean;
}

// API設定（環境変数などから取得するのが望ましい）
const API_CONFIG = {
  baseUrl: process.env.REACT_APP_API_BASE_URL || "https://flux-worker.kgu-ishizaka.workers.dev",
  token: process.env.REACT_APP_API_TOKEN || "default-token",
};

const ImageGenerationScreen: React.FC<ImageGenerationScreenProps> = ({
  onBack,
  userId,
}) => {
  const [prompt, setPrompt] = useState<string>("");
  const [negativePrompt, setNegativePrompt] = useState<string>("");
  const [selectedSize, setSelectedSize] = useState<string>("1024x1024");
  const [style, setStyle] = useState<string>("natural");
  const [quality, setQuality] = useState<string>("standard");
  const [guidanceScale, setGuidanceScale] = useState<number>(7.5);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [generationHistory, setGenerationHistory] = useState<
    GenerationHistoryItem[]
  >([]);
  const [apiClient, setApiClient] = useState<ApiClient | null>(null);

  // コンポーネントのマウント時にAPIクライアントを初期化
  useEffect(() => {
    // APIクライアントの初期化
    const client = new ApiClient(API_CONFIG, userId);
    setApiClient(client);
  }, [userId]);

  // スタイルのプリセット - プロンプトに適用するためのスタイル
  const styleOptions: StyleOption[] = [
    { id: "natural", name: "自然" },
    { id: "vivid", name: "鮮やか" },
    { id: "anime", name: "アニメ" },
    { id: "illustration", name: "イラスト" },
    { id: "photographic", name: "写真" },
    { id: "digital-art", name: "デジタルアート" },
  ];

  // 画像サイズのオプション - Fluxモデル対応サイズ
  const sizeOptions: SizeOption[] = [
    { id: "1024x1024", name: "正方形 (1:1)", aspect: "1:1", width: 1024, height: 1024 },
    { id: "1024x1792", name: "縦長 (9:16)", aspect: "9:16", width: 1024, height: 1792 },
    { id: "1792x1024", name: "横長 (16:9)", aspect: "16:9", width: 1792, height: 1024 },
  ];

  // クオリティオプション - Fluxモデルのステップ数
  const qualityOptions: QualityOption[] = [
    { id: "standard", name: "標準", steps: 25 },
    { id: "hd", name: "高画質 (HD)", steps: 50 },
  ];

  // 選択されたサイズの詳細を取得する関数
  const getSelectedSizeDetails = (): { width: number; height: number } => {
    const selectedSizeOption = sizeOptions.find(option => option.id === selectedSize);
    return {
      width: selectedSizeOption?.width || 1024,
      height: selectedSizeOption?.height || 1024
    };
  };

  // 選択された品質レベルのステップ数を取得する関数
  const getInferenceSteps = (): number => {
    const selectedQualityOption = qualityOptions.find(option => option.id === quality);
    return selectedQualityOption?.steps || 25;
  };

  // スタイルをプロンプトに適用する関数
  const applyStyleToPrompt = (basePrompt: string, styleId: string): string => {
    // スタイルに基づいてプロンプトを拡張
    let styledPrompt = basePrompt;
    
    switch (styleId) {
      case "vivid":
        styledPrompt += ", vibrant colors, high contrast";
        break;
      case "anime":
        styledPrompt += ", anime style, cel shading, manga illustration";
        break;
      case "illustration":
        styledPrompt += ", detailed illustration, artistic, hand-drawn";
        break;
      case "photographic":
        styledPrompt += ", photorealistic, detailed, high resolution photograph";
        break;
      case "digital-art":
        styledPrompt += ", digital art, detailed, fantasy art, trending on artstation";
        break;
      // naturalの場合はデフォルトのままで良い
    }
    
    return styledPrompt;
  };

  // ApiClientを使用して画像を生成する
  const generateImageWithAPI = async (): Promise<void> => {
    if (!prompt || !apiClient) return;

    setIsGenerating(true);
    setError(null);

    try {
      // サイズ詳細を取得
      const { width, height } = getSelectedSizeDetails();
      
      // プロンプトにスタイルを適用
      const styledPrompt = applyStyleToPrompt(prompt, style);
      
      // Fluxモデル用のリクエストパラメータを構築
      const requestData: FluxRequestData = {
        prompt: styledPrompt,
        negative_prompt: negativePrompt,
        width,
        height,
        num_outputs: 1,
        scheduler: "K_EULER_ANCESTRAL", // デフォルトスケジューラー
        num_inference_steps: getInferenceSteps(),
        guidance_scale: guidanceScale,
        prompt_upsampling: true
      };

      // 直接fetchを使用してFlux Workerにリクエスト
      const response = await fetch(API_CONFIG.baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${API_CONFIG.token}`
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "画像の生成に失敗しました");
      }

      const result = await response.json();

      // レスポンスの確認
      if (!result.success || !result.images || result.images.length === 0) {
        throw new Error(result.error || "画像の生成に失敗しました");
      }

      // 生成された画像のURLを取得
      const imageUrl = result.images[0];

      // 生成結果をセット
      setGeneratedImage(imageUrl);

      // 履歴に追加
      setGenerationHistory((prev: GenerationHistoryItem[]) =>
        [{ url: imageUrl, prompt }, ...prev].slice(0, 10)
      ); // 最新10件のみ保持
    } catch (error) {
      console.error("画像生成エラー:", error);
      setError(
        (error as Error).message ||
          "画像の生成中にエラーが発生しました。もう一度お試しください。"
      );
    } finally {
      setIsGenerating(false);
    }
  };

  // 画像の保存
  const handleSaveImage = (): void => {
    if (!generatedImage) return;

    // ファイル名を作成
    const fileName = `saku-ai-image-${new Date().getTime()}.png`;

    // ダウンロードリンクを作成して自動クリック
    const link = document.createElement("a");
    link.href = generatedImage;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // プロンプトをコピー
  const handleCopyPrompt = (): void => {
    navigator.clipboard.writeText(prompt);
    alert("プロンプトをコピーしました");
  };

  const handlePromptChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    setPrompt(e.target.value);
  };

  const handleNegativePromptChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    setNegativePrompt(e.target.value);
  };

  const handleSizeChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    setSelectedSize(e.target.value);
  };

  const handleStyleChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    setStyle(e.target.value);
  };

  const handleGuidanceScaleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setGuidanceScale(parseFloat(e.target.value));
  };

  return (
    <div className="bg-gray-100">
      <div className="bg-white px-4 py-3 flex items-center shadow">
        <button onClick={onBack} className="text-blue-500 mr-3">
          <ChevronLeft size={24} />
        </button>
        <h2 className="text-lg font-bold">AI画像生成</h2>
      </div>

      <div className="p-4">
        {/* プロンプト入力エリア */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2 text-gray-700">
            プロンプト
          </label>
          <textarea
            className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all"
            placeholder="生成したい画像の詳細な説明を入力してください（例：青い空を背景に、花畑の中で微笑む少女）"
            value={prompt}
            onChange={handlePromptChange}
            rows={4}
          />
        </div>

        {/* 基本パラメータ */}
        <div className="mb-4 grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">
              サイズ
            </label>
            <select
              className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              value={selectedSize}
              onChange={handleSizeChange}
            >
              {sizeOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">
              スタイル
            </label>
            <select
              className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              value={style}
              onChange={handleStyleChange}
            >
              {styleOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* 詳細設定トグル */}
        <button
          className="w-full flex items-center justify-center py-2 text-blue-500 text-sm font-medium mb-4"
          onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
        >
          詳細設定
          <Sliders size={14} className="ml-1" />
        </button>

        {/* 詳細設定パネル */}
        {isAdvancedOpen && (
          <div className="mb-4 bg-white rounded-lg shadow-md p-4">
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2 text-gray-700">
                ネガティブプロンプト
              </label>
              <textarea
                className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all"
                placeholder="生成したくない要素を指定（例：ぼやけた画像、低品質、歪み）"
                value={negativePrompt}
                onChange={handleNegativePromptChange}
                rows={2}
              />
            </div>

            <div className="mb-4">
              <label className="flex justify-between text-sm font-medium mb-1 text-gray-700">
                <span>ガイダンススケール ({guidanceScale.toFixed(1)})</span>
                <span className="text-gray-500 text-xs">
                  低い値: 創造性、高い値: プロンプト忠実性
                </span>
              </label>
              <input
                type="range"
                min="1"
                max="20"
                step="0.5"
                value={guidanceScale}
                onChange={handleGuidanceScaleChange}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                画質
              </label>
              <div className="flex space-x-2">
                {qualityOptions.map((option) => (
                  <button
                    key={option.id}
                    className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
                      quality === option.id
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                    onClick={() => setQuality(option.id)}
                  >
                    {option.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 生成ボタンと使用クレジット */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center text-gray-500 text-sm">
              <CreditCard size={16} className="mr-1" />
              <span>使用クレジット: {quality === "hd" ? 2 : 1}</span>
            </div>
            {quality === "hd" && (
              <div className="text-amber-600 text-sm font-medium">
                HDは2倍のクレジットを使用
              </div>
            )}
          </div>

          <button
            className={`w-full py-3 rounded-lg font-medium text-white ${
              isGenerating || !prompt
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
            }`}
            onClick={generateImageWithAPI}
            disabled={isGenerating || !prompt}
          >
            {isGenerating ? (
              <span className="flex items-center justify-center">
                <Loader size={20} className="animate-spin mr-2" />
                生成中...
              </span>
            ) : (
              "画像を生成"
            )}
          </button>
        </div>

        {/* エラーメッセージ */}
        {error && (
          <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-4 rounded">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* 生成結果 */}
        {generatedImage && (
          <div className="bg-white rounded-lg shadow-lg p-4 mb-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-medium">生成結果</h3>
              <div className="flex space-x-2">
                <button
                  className="text-blue-500 p-1 hover:bg-blue-50 rounded-md transition-colors"
                  onClick={handleCopyPrompt}
                  title="プロンプトをコピー"
                >
                  <Copy size={18} />
                </button>
                <button
                  className="text-blue-500 p-1 hover:bg-blue-50 rounded-md transition-colors"
                  onClick={generateImageWithAPI}
                  title="再生成"
                  disabled={isGenerating}
                >
                  <RefreshCw
                    size={18}
                    className={isGenerating ? "animate-spin" : ""}
                  />
                </button>
                <button
                  className="text-blue-500 p-1 hover:bg-blue-50 rounded-md transition-colors"
                  onClick={handleSaveImage}
                  title="ダウンロード"
                >
                  <Download size={18} />
                </button>
              </div>
            </div>

            <div className="rounded-lg overflow-hidden border border-gray-200">
              <img
                src={generatedImage}
                alt="AI生成画像"
                className="w-full h-auto"
              />
            </div>

            <div className="mt-3 text-sm text-gray-600">
              <p className="line-clamp-2">{prompt}</p>
            </div>
          </div>
        )}

        {/* 生成履歴 */}
        {generationHistory.length > 0 && (
          <div className="mb-4 bg-white rounded-lg shadow-lg p-4">
            <h3 className="font-medium mb-3">最近の生成履歴</h3>
            <div className="grid grid-cols-3 gap-2">
              {generationHistory.slice(0, 6).map((item, index) => (
                <div
                  key={index}
                  className="aspect-square rounded-md overflow-hidden relative cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => {
                    setGeneratedImage(item.url);
                    setPrompt(item.prompt);
                  }}
                >
                  <img
                    src={item.url}
                    alt={`生成履歴 ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* スタイルプレビュー */}
        <div className="bg-white rounded-lg shadow-lg p-4">
          <h3 className="font-medium mb-3">スタイルサンプル</h3>
          <div className="grid grid-cols-3 gap-2">
            {styleOptions.map((styleOption) => (
              <div
                key={styleOption.id}
                className={`aspect-square rounded-md overflow-hidden relative cursor-pointer ${
                  style === styleOption.id ? "ring-2 ring-blue-500" : ""
                }`}
                onClick={() => setStyle(styleOption.id)}
              >
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${
                    styleOption.id === "natural"
                      ? "from-green-400 to-blue-500"
                      : styleOption.id === "vivid"
                      ? "from-pink-400 to-yellow-500"
                      : styleOption.id === "anime"
                      ? "from-blue-400 to-purple-500"
                      : styleOption.id === "illustration"
                      ? "from-orange-400 to-red-500"
                      : styleOption.id === "photographic"
                      ? "from-gray-400 to-gray-600"
                      : "from-indigo-400 to-purple-500"
                  } opacity-80`}
                ></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-white text-xs font-medium">
                    {styleOption.name}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageGenerationScreen;
